import { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import type { Toast } from '@/types/api';

interface ToasterProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

let toastCounter = 0;
const toasts: Toast[] = [];
const listeners: Array<(toasts: Toast[]) => void> = [];

// Toast management functions
export const toast = {
  success: (message: string, duration = 5000) => {
    addToast({ message, type: 'success', duration });
  },
  error: (message: string, duration = 8000) => {
    addToast({ message, type: 'error', duration });
  },
  warning: (message: string, duration = 6000) => {
    addToast({ message, type: 'warning', duration });
  },
  info: (message: string, duration = 4000) => {
    addToast({ message, type: 'info', duration });
  },
};

function addToast(toast: Omit<Toast, 'id'>) {
  const newToast: Toast = {
    ...toast,
    id: String(++toastCounter),
  };
  
  toasts.push(newToast);
  notifyListeners();
  
  if (toast.duration) {
    setTimeout(() => removeToast(newToast.id), toast.duration);
  }
}

function removeToast(id: string) {
  const index = toasts.findIndex(t => t.id === id);
  if (index > -1) {
    toasts.splice(index, 1);
    notifyListeners();
  }
}

function notifyListeners() {
  listeners.forEach(listener => listener([...toasts]));
}

function subscribe(listener: (toasts: Toast[]) => void) {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
}

export function Toaster({ position = 'top-right' }: ToasterProps) {
  const [currentToasts, setCurrentToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const unsubscribe = subscribe(setCurrentToasts);
    return unsubscribe;
  }, []);

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
  };

  const getIcon = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getToastClasses = (type: Toast['type']) => {
    const baseClasses = "relative p-4 rounded-lg shadow-lg border max-w-sm w-full transform transition-all duration-300 ease-in-out";
    
    switch (type) {
      case 'success':
        return `${baseClasses} bg-white border-green-200`;
      case 'error':
        return `${baseClasses} bg-white border-red-200`;
      case 'warning':
        return `${baseClasses} bg-white border-orange-200`;
      case 'info':
        return `${baseClasses} bg-white border-blue-200`;
      default:
        return `${baseClasses} bg-white border-gray-200`;
    }
  };

  if (currentToasts.length === 0) {
    return null;
  }

  return (
    <div className={`fixed z-50 ${positionClasses[position]} space-y-2`}>
      {currentToasts.map(toast => (
        <div
          key={toast.id}
          className={getToastClasses(toast.type)}
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-start space-x-3">
            {getIcon(toast.type)}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">
                {toast.message}
              </p>
            </div>
            <button
              onClick={() => removeToast(toast.id)}
              className="flex-shrink-0 p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close notification"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}